import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'modules/auth/context';
import { getMistakes, retryMistake, revealMistake, setMistakeLearned, type MistakeList, type RetryAnswer } from './api';

const mistakesKey = (userId?: string) => ['portal-mistakes', userId] as const;

export function useMistakes() {
  const { state } = useAuthContext();
  return useQuery({
    queryKey: mistakesKey(state.user?.id),
    queryFn: ({ signal }) => getMistakes(signal),
    enabled: Boolean(state.user?.id),
  });
}

/** Marks a mistake learned: it leaves the list at once and comes back if the request fails. */
export function useMarkLearned() {
  const { state } = useAuthContext();
  const client = useQueryClient();
  const key = mistakesKey(state.user?.id);
  return useMutation({
    mutationFn: (mistakeKey: string) => setMistakeLearned(mistakeKey, true),
    onMutate: async (mistakeKey) => {
      await client.cancelQueries({ queryKey: key });
      const previous = client.getQueryData<MistakeList>(key);
      if (previous) {
        client.setQueryData<MistakeList>(key, {
          ...previous,
          items: previous.items.filter((m) => m.key !== mistakeKey),
          mastered: previous.mastered + 1,
        });
      }
      return { previous };
    },
    onError: (_error, _mistakeKey, context) => {
      if (context?.previous) client.setQueryData(key, context.previous);
    },
  });
}

/**
 * Answers a mistake again. A fixed card stays on screen to show its result; only the totals move now,
 * and the next load leaves it out.
 */
export function useRetryMistake() {
  const { state } = useAuthContext();
  const client = useQueryClient();
  const key = mistakesKey(state.user?.id);
  return useMutation({
    mutationFn: ({ mistakeKey, answer }: { mistakeKey: string; answer: RetryAnswer }) => retryMistake(mistakeKey, answer),
    onSuccess: (result) => {
      if (!result.correct) return;
      client.setQueryData<MistakeList>(key, (previous) => previous && {
        ...previous,
        fixed: previous.fixed + 1,
        mastered: previous.mastered + 1,
        points: previous.points + result.pointsAwarded,
      });
    },
  });
}

/** Shows the right answer; the card keeps it, and the mistake stays in the bank without points. */
export function useRevealMistake() {
  const { state } = useAuthContext();
  const client = useQueryClient();
  const key = mistakesKey(state.user?.id);
  return useMutation({
    mutationFn: (mistakeKey: string) => revealMistake(mistakeKey),
    onSuccess: (answer, mistakeKey) => {
      client.setQueryData<MistakeList>(key, (previous) => previous && {
        ...previous,
        items: previous.items.map((m) => (m.key === mistakeKey ? { ...m, ...answer, revealed: true } : m)),
      });
    },
  });
}
